import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Paper, CircularProgress, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ModernSelect from "../ModernSelect";
import { LANGUAGES } from "../FlagIcons";
import ConfirmationModal from "../ConfirmationModal";
import UnsavedChangesHint from "../UnsavedChangesHint";
import { tokens } from "../Triage/styles";
import { useReportDirty } from "../../hooks/useLeaveGuard";

// Máscaras (mesma abordagem do checkout: guarda só dígitos, formata na exibição).
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const applyMask = (digits, mask) => {
  let out = "", di = 0;
  for (const ch of mask) {
    if (di >= digits.length) break;
    if (ch === "#") out += digits[di++];
    else out += ch;
  }
  return out;
};
const fmtCnpj = (v) => applyMask(onlyDigits(v).slice(0, 14), "##.###.###/####-##");
const fmtPhone = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  return applyMask(d, d.length <= 10 ? "(##) ####-####" : "(##) #####-####");
};

// O título e a descrição vêm do cabeçalho da página de Configurações.
const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const border = c.inputBorder;
  return {
    card: {
      padding: theme.spacing(2.5),
      borderRadius: 12,
      background: c.surface,
      border: `1px solid ${c.border}`,
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(1.75),
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    rowGap: { marginTop: theme.spacing(1.75) },
    field: { display: "flex", flexDirection: "column", gap: 5, minWidth: 0 },
    label: { fontSize: "0.8rem", fontWeight: 600, color: dark ? "#f8fafc" : "#111827" },
    input: {
      height: 42,
      borderRadius: 8,
      border: `1px solid ${border}`,
      background: dark ? "rgba(255,255,255,0.05)" : "#fff",
      color: dark ? "#f8fafc" : "#111827",
      padding: "0 12px",
      fontSize: "0.9rem",
      fontFamily: "inherit",
      width: "100%",
      boxSizing: "border-box",
      outline: "none",
      transition: "border-color 120ms",
      "&:focus": { borderColor: "#3D5CFD" },
      "&::placeholder": { color: dark ? "rgba(248,250,252,0.35)" : "#9ca3af" },
    },
    hint: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(1.5),
      fontSize: "0.78rem",
      lineHeight: 1.5,
    },
    actions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(2.5),
    },
    primaryBtn: {
      borderRadius: 8,
      border: "none",
      height: 40,
      padding: "0 22px",
      cursor: "pointer",
      fontSize: "0.88rem",
      fontWeight: 700,
      fontFamily: "inherit",
      color: "#fff",
      background: "#3D5CFD",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      transition: "opacity 120ms",
      "&:hover": { opacity: 0.92 },
      "&:disabled": { opacity: 0.6, cursor: "default" },
    },
  };
});

const emptyForm = { razaoSocial: "", nomeFantasia: "", cnpj: "", phone: "", email: "", language: "pt" };

// Campos do formulário a partir da conta salva (também é a base para saber se
// há alterações não salvas).
const toForm = (c) =>
  c
    ? {
        razaoSocial: c.razaoSocial || "",
        nomeFantasia: c.nomeFantasia || "",
        cnpj: onlyDigits(c.cnpj || ""),
        phone: onlyDigits(c.phone || ""),
        email: c.email || "",
        language: LANGUAGES.some(({ code }) => code === c.language) ? c.language : "pt",
      }
    : emptyForm;

// onDirtyChange(bool), opcional: avisa quando há alterações não salvas.
const CompanyDataForm = ({ company, onSaved, onDirtyChange }) => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState(company?.id || null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Conta escolhida no seletor enquanto havia edição não salva (pede confirmação).
  const [pendingAccountId, setPendingAccountId] = useState(null);


  // Depende só do id: salvar atualiza o objeto da empresa no pai, e refazer o
  // GET aqui trocava o formulário por um spinner a cada gravação.
  const companyId = company?.id;
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/companies/group-companies");
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setCompanies(list);
        const initialId =
          companyId && list.some((c) => c.id === companyId)
            ? companyId
            : list[0]?.id ?? null;
        setSelectedId(initialId);
        setForm(toForm(list.find((c) => c.id === initialId)));
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [companyId]);

  const selected = companies.find((c) => c.id === selectedId) || null;
  const dirty = !loading && !!selected && JSON.stringify(form) !== JSON.stringify(toForm(selected));
  useReportDirty(dirty, onDirtyChange);

  const switchAccount = (id) => {
    setSelectedId(id);
    setForm(toForm(companies.find((c) => c.id === id)));
  };

  // Trocar de conta com edição pendente perderia o que foi digitado: pergunta antes.
  const handleSelect = (id) => {
    if (id === selectedId) return;
    if (dirty) setPendingAccountId(id);
    else switchAccount(id);
  };

  const setField = (key, raw) =>
    setForm((f) => ({ ...f, [key]: key === "cnpj" || key === "phone" ? onlyDigits(raw) : raw }));

  const handleSave = async () => {
    if (!selectedId) return;
    const savedId = selectedId;
    const submitted = form;
    setSaving(true);
    try {
      const payload = {
        razaoSocial: form.razaoSocial.trim(),
        nomeFantasia: form.nomeFantasia.trim(),
        cnpj: form.cnpj,
        phone: form.phone,
        email: form.email.trim(),
        language: form.language,
      };
      const { data } = await api.put(`/companies/${savedId}/company-data`, payload);
      setCompanies((prev) => prev.map((c) => (c.id === savedId ? { ...c, ...data } : c)));
      // O formulário passa a mostrar o que foi salvo (sem os espaços aparados),
      // a não ser que a pessoa tenha digitado mais enquanto gravava: aí o que
      // ela digitou fica, como alteração não salva.
      setForm((current) => (current === submitted ? toForm({ ...selected, ...data }) : current));
      toast.success(i18n.t("settings.companyData.saved"));
      if (onSaved) onSaved(data);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "ERR_INVALID_CNPJ") {
        toast.error(i18n.t("settings.companyData.invalidCnpj"));
      } else {
        toastError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  return (
    <Paper className={classes.card} elevation={0}>
      <ConfirmationModal
        title={i18n.t("unsavedChanges.title")}
        open={pendingAccountId !== null}
        onClose={() => setPendingAccountId(null)}
        onConfirm={() => switchAccount(pendingAccountId)}
      >
        {i18n.t("unsavedChanges.body")}
      </ConfirmationModal>
      {companies.length > 1 && (
        <div className={`${classes.field}`} style={{ marginBottom: 18 }}>
          <span className={classes.label}>{i18n.t("settings.companyData.accountLabel")}</span>
          <ModernSelect
            size="small"
            // Trocar de conta no meio da gravação misturaria os dados das duas.
            disabled={saving}
            value={selectedId || ""}
            onChange={(e) => handleSelect(Number(e.target.value))}
            MenuProps={{ disableScrollLock: true }}
            formControlProps={{ fullWidth: true }}
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </ModernSelect>
        </div>
      )}

      <div className={classes.grid2}>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.razaoSocial")}</span>
          <input
            className={classes.input}
            value={form.razaoSocial}
            onChange={(e) => setField("razaoSocial", e.target.value)}
            placeholder={i18n.t("settings.companyData.phRazaoSocial")}
            autoComplete="off"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.nomeFantasia")}</span>
          <input
            className={classes.input}
            value={form.nomeFantasia}
            onChange={(e) => setField("nomeFantasia", e.target.value)}
            placeholder={i18n.t("settings.companyData.phNomeFantasia")}
            autoComplete="off"
          />
        </div>
      </div>

      <div className={`${classes.grid2} ${classes.rowGap}`}>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.cnpj")}</span>
          <input
            className={classes.input}
            inputMode="numeric"
            value={fmtCnpj(form.cnpj)}
            onChange={(e) => setField("cnpj", e.target.value)}
            placeholder="00.000.000/0000-00"
            autoComplete="off"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.phone")}</span>
          <input
            className={classes.input}
            inputMode="numeric"
            value={fmtPhone(form.phone)}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="(11) 99999-9999"
            autoComplete="off"
          />
        </div>
      </div>

      <div className={`${classes.grid2} ${classes.rowGap}`}>
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.email")}</span>
          <input
            className={classes.input}
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder={i18n.t("settings.companyData.phEmail")}
            autoComplete="off"
          />
        </div>
        {/* O idioma das mensagens automáticas aos clientes; o da tela é de cada pessoa. */}
        <div className={classes.field}>
          <span className={classes.label}>{i18n.t("settings.companyData.language")}</span>
          <ModernSelect
            size="small"
            value={form.language}
            onChange={(e) => setField("language", e.target.value)}
            MenuProps={{ disableScrollLock: true }}
            formControlProps={{ fullWidth: true }}
          >
            {LANGUAGES.map(({ code, name }) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </ModernSelect>
        </div>
      </div>

      {/* A cobrança da Empresa usa os dados de Faturamento (a antiga "conta principal de cobrança" saiu na etapa 12). */}
      <div className={classes.hint}>{i18n.t("settings.companyData.billingHint")}</div>

      <div className={classes.actions}>
        <UnsavedChangesHint show={dirty} />
        <button className={classes.primaryBtn} onClick={handleSave} disabled={saving || !dirty}>
          {saving && <CircularProgress size={15} color="inherit" />}
          {i18n.t("settings.companyData.save")}
        </button>
      </div>
    </Paper>
  );
};

export default CompanyDataForm;
